// Shared display formatters for the things that are easy to render into a
// lie: a duration, a small amount of money, and (below) an invoice's id.

/** A human duration: 45s · 1m 30s · 2h 05m. `fallback` covers null/0/garbage.
 *
 * The generation card printed `${seconds}s` raw, so a half-hour generation read
 * as "2211s" — a number nobody converts in their head. Hours matter too: a long
 * crawl otherwise reads as "126m".
 */
export function formatDuration(seconds, fallback = "—") {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return fallback;
  if (s < 60) return s < 10 ? `${s.toFixed(1)}s` : `${Math.round(s)}s`;
  const total = Math.round(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m ${String(sec).padStart(2, "0")}s`;
}

/** A dollar amount that never rounds a real cost down to nothing.
 *
 * `toFixed(3)` turned every generation cheaper than half a tenth of a cent into
 * "$0.000", which reads as free. A cost that exists is always shown as existing:
 * below the smallest printable amount it becomes "<$0.001".
 */
export function formatUsd(value, fallback = "—") {
  // Guarded before Number(): Number(null) and Number("") are both 0, which would
  // render a MISSING cost as a confident "$0.00" — the same lie in a new shape.
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  if (n === 0) return "$0.00";
  const abs = Math.abs(n);
  if (abs < 0.001) return "<$0.001";
  if (abs < 1) return `$${n.toFixed(3)}`;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** The short id shown for an invoice — there is no real sequential invoice
 * number in this system yet, so this is just a stable slice of the row's own
 * database id. The tester's own print-invoice view and the superadmin's
 * invoice table used to slice it two DIFFERENT ways (first 10 chars, lowercase
 * vs. last 8, uppercase), so the exact same invoice showed two different
 * "IDs" depending which panel you looked at it from. One function, used in
 * both places, is what keeps that from drifting apart again.
 */
export function invoiceNumber(id) {
  return String(id || "").slice(-8).toUpperCase();
}
